const PARAGRAPH_BLOCK_RE = /<<<PARAGRAPH_BEGIN(?::([a-z0-9-_]+))?>>>([\s\S]*?)<<<PARAGRAPH_END>>>/i;
const MARKER_GLOBAL_RE = /<<<[A-Z_]+(?::[a-z0-9-_]+)?>>>/g;

interface ParsedParagraphResponse {
  chat: string;
  body?: string;
}

const stripMarkers = (text: string): string =>
  text
    .replace(PARAGRAPH_BLOCK_RE, '')
    .replace(MARKER_GLOBAL_RE, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const looksLikeConversationalReply = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed) return true;
  if (
    /^(here('s| is)|i('ve| have)|sure|certainly|let me|of course|absolutely|great question|good question)/i.test(
      trimmed
    )
  ) {
    return true;
  }
  if (/\?\s*$/.test(trimmed) && trimmed.length < 400) return true;
  return false;
};

const looksLikeParagraphProse = (text: string): boolean => {
  const trimmed = text.trim();
  if (trimmed.length < 80) return false;
  if (looksLikeConversationalReply(trimmed)) return false;
  return true;
};

const DRAFT_NOTES_SPLIT_RE = /\n\s*draft notes:\s*/i;

const extractParagraphBodyFromUnmarked = (raw: string): string | undefined => {
  const withoutNotes = raw.split(DRAFT_NOTES_SPLIT_RE)[0]?.trim() ?? '';
  const withoutPreamble = withoutNotes
    .replace(/^here(?:'s| is)\s+(?:a\s+)?draft\s+of[\s\S]*?\n\n/i, '')
    .replace(/^here(?:'s| is)\s+(?:a\s+)?draft\s+of[^\n]+\n/i, '')
    .trim();

  if (withoutPreamble.length >= 80 && !looksLikeConversationalReply(withoutPreamble)) {
    return withoutPreamble;
  }
  return undefined;
};

const parseParagraphResponse = (raw: string): ParsedParagraphResponse => {
  const text = raw ?? '';
  const blockMatch = text.match(PARAGRAPH_BLOCK_RE);

  if (blockMatch) {
    const body = blockMatch[2].trim();
    const chat = stripMarkers(text);
    return {
      chat: chat || (body ? 'Paragraph updated.' : ''),
      body: body || undefined,
    };
  }

  const trimmed = text.trim();
  const extractedBody = extractParagraphBodyFromUnmarked(trimmed);
  if (extractedBody) {
    return { chat: 'Paragraph updated.', body: extractedBody };
  }

  if (looksLikeConversationalReply(trimmed)) {
    return { chat: trimmed };
  }

  if (looksLikeParagraphProse(trimmed)) {
    return { chat: 'Paragraph updated.', body: trimmed };
  }

  return { chat: trimmed };
};

export { parseParagraphResponse };
